/* eslint-disable react/no-array-index-key */
import { FormattedMessage, FormattedHTMLMessage, injectIntl } from 'react-intl';
import React from 'react';
import classNames from 'classnames';
import {
  EditListingSection,
  FieldLabel,
  FieldNumberInput,
  InlineTextButton,
} from '../../components';
import { flexiblePricingConfig } from '../../marketplace-custom-config';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import config from '../../config';
import { ReactComponent as RefreshIcon } from '../../assets/icons/icon-reset.svg';
import css from './FieldFlexiblePricing.module.css';
import { getFlexiblePricingData } from '../../util/package';

const { Money } = sdkTypes;

const FlexiblePricing = ({ intl, values, form }) => {
  // Handle case where price might be a Money object or a number
  const priceValue = values?.price;
  const basePrice =
    priceValue && typeof priceValue === 'object' && priceValue.amount
      ? priceValue.amount
      : priceValue || 0;

  let basePriceAmount;

  if (basePrice instanceof Money) {
    basePriceAmount = basePrice.amount;
  } else {
    basePriceAmount = basePrice;
  }

  const basePriceMoney = new Money(basePriceAmount, config.currency);

  // Calculate discounted price based on discount percentage
  const calculateDiscountedPrice = discount => {
    const discountAmount = (basePriceAmount * discount) / 100;
    const discountedPrice = basePriceAmount - discountAmount;
    return new Money(Math.max(0, discountedPrice), config.currency);
  };

  // Get form values for flexible pricing
  const flexiblePricingData = getFlexiblePricingData(values?.flexiblePricing);
  const adultNonHunterDiscount = flexiblePricingData.adultNonHunter.discount;
  const youthHunterDiscount = flexiblePricingData.youthHunter.discount;
  const youthNonHunterDiscount = flexiblePricingData.youthNonHunter.discount;

  return (
    <>
      <div className={css.fieldHeader}>
        <FormattedMessage id="EditListingCreatePackageForm.flexiblePricingSectionHeader" />
      </div>
      <p className={css.fieldSubheader}>
        <FormattedHTMLMessage id="EditListingCreatePackageForm.flexiblePricingSectionHelp" />
      </p>

      <EditListingSection
        hasBackground
        hasBorders
        isFullWidth
        rootClassName={css.pricingTierContainerWrapper}
      >
        {/* Adult Hunter - Base Price */}
        <div className={classNames(css.pricingTierContainer, css.pricingTierContainerReadonly)}>
          <div className={css.pricingTierHeader}>
            <FieldLabel className={css.pricingTierTitle}>
              <FormattedMessage id="EditListingCreatePackageForm.adultHunterLabel" />
            </FieldLabel>
            <div className={css.pricingTierSubtitle}>
              <FormattedMessage id="EditListingCreatePackageForm.basePriceLabel" />
            </div>
          </div>

          <div className={css.pricingTierPriceWrapper}>
            <div className={css.pricingTierPrice}>{formatMoney(intl, basePriceMoney)}</div>
            <span className={css.pricingTierPriceUnit}>
              <FormattedMessage id="EditListingCreatePackageForm.perAdultHunterLabel" />
            </span>
          </div>
        </div>
      </EditListingSection>

      {/* Adult Non-Hunter */}
      <EditListingSection hasBorders isFullWidth rootClassName={css.pricingTierContainerWrapper}>
        <div className={css.pricingTierContainer}>
          <div className={css.pricingTierContainerLeft}>
            <div className={css.pricingTierHeader}>
              <FieldLabel className={css.pricingTierTitle}>
                <FormattedMessage id="EditListingCreatePackageForm.adultNonHunterLabel" />
              </FieldLabel>
              <div className={classNames(css.pricingTierSubtitle, css.pricingTierSubtitleLight)}>
                <FormattedMessage
                  id="EditListingCreatePackageForm.agesLabel"
                  values={{ ages: '16+' }}
                />
              </div>
            </div>

            <div className={css.pricingTierControls}>
              <div className={css.pricingControlGroup}>
                <FieldLabel rootClassName={css.pricingControlLabel}>
                  <FormattedMessage id="EditListingCreatePackageForm.discountLabel" />
                </FieldLabel>
                <div className={css.percentageInputWrapper}>
                  <FieldNumberInput
                    className={css.percentageInput}
                    name="flexiblePricing.adultNonHunter.discount"
                    min={0}
                    max={100}
                    placeholder="75"
                    hideButtons
                  />
                  <span className={css.percentageSymbol}>%</span>
                </div>
              </div>
              <div className={css.pricingControlGroup}>
                <FieldLabel rootClassName={css.pricingControlLabel}>
                  <FormattedMessage id="EditListingCreatePackageForm.flexiblePricingQuantity" />
                </FieldLabel>
                <div className={css.quantityControls}>
                  <FieldNumberInput
                    className={css.quantityInput}
                    name="flexiblePricing.adultNonHunter.quantity"
                    min={0}
                    placeholder="1"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className={css.pricingTierPriceWrapper}>
            <div
              className={classNames(css.pricingTierPrice, {
                [css.pricingTierPriceDisabled]: flexiblePricingData.adultNonHunter.quantity === 0,
              })}
            >
              {formatMoney(intl, calculateDiscountedPrice(adultNonHunterDiscount))}
            </div>
            <span
              className={classNames(css.pricingTierPriceUnit, {
                [css.pricingTierPriceUnitDisabled]:
                  flexiblePricingData.adultNonHunter.quantity === 0,
              })}
            >
              <FormattedMessage id="EditListingCreatePackageForm.perAdultNonHunterLabel" />
            </span>
          </div>
        </div>
      </EditListingSection>

      {/* Youth Hunter */}
      <EditListingSection hasBorders isFullWidth rootClassName={css.pricingTierContainerWrapper}>
        <div className={css.pricingTierContainer}>
          <div className={css.pricingTierContainerLeft}>
            <div className={css.pricingTierHeader}>
              <FieldLabel className={css.pricingTierTitle}>
                <FormattedMessage id="EditListingCreatePackageForm.youthHunterLabel" />
              </FieldLabel>
              <div className={classNames(css.pricingTierSubtitle, css.pricingTierSubtitleLight)}>
                <FormattedMessage
                  id="EditListingCreatePackageForm.agesLabel"
                  values={{ ages: '3-15' }}
                />
              </div>
            </div>

            <div className={css.pricingTierControls}>
              <div className={css.pricingControlGroup}>
                <FieldLabel rootClassName={css.pricingControlLabel}>
                  <FormattedMessage id="EditListingCreatePackageForm.discountLabel" />
                </FieldLabel>
                <div className={css.percentageInputWrapper}>
                  <FieldNumberInput
                    className={css.percentageInput}
                    name="flexiblePricing.youthHunter.discount"
                    min={0}
                    max={100}
                    placeholder="50"
                    hideButtons
                  />
                  <span className={css.percentageSymbol}>%</span>
                </div>
              </div>
              <div className={css.pricingControlGroup}>
                <FieldLabel rootClassName={css.pricingControlLabel}>
                  <FormattedMessage id="EditListingCreatePackageForm.flexiblePricingQuantity" />
                </FieldLabel>
                <div className={css.quantityControls}>
                  <FieldNumberInput
                    className={css.quantityInput}
                    name="flexiblePricing.youthHunter.quantity"
                    min={0}
                    placeholder="1"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className={css.pricingTierPriceWrapper}>
            <div
              className={classNames(css.pricingTierPrice, {
                [css.pricingTierPriceDisabled]: flexiblePricingData.youthHunter.quantity === 0,
              })}
            >
              {formatMoney(intl, calculateDiscountedPrice(youthHunterDiscount))}
            </div>
            <span
              className={classNames(css.pricingTierPriceUnit, {
                [css.pricingTierPriceUnitDisabled]: flexiblePricingData.youthHunter.quantity === 0,
              })}
            >
              <FormattedMessage id="EditListingCreatePackageForm.perYouthHunterLabel" />
            </span>
          </div>
        </div>
      </EditListingSection>

      {/* Youth Non-Hunter */}
      <EditListingSection hasBorders isFullWidth rootClassName={css.pricingTierContainerWrapper}>
        <div className={css.pricingTierContainer}>
          <div className={css.pricingTierContainerLeft}>
            <div className={css.pricingTierHeader}>
              <FieldLabel className={css.pricingTierTitle}>
                <FormattedMessage id="EditListingCreatePackageForm.youthNonHunterLabel" />
              </FieldLabel>
              <div className={classNames(css.pricingTierSubtitle, css.pricingTierSubtitleLight)}>
                <FormattedMessage
                  id="EditListingCreatePackageForm.agesLabel"
                  values={{ ages: '3-15' }}
                />
              </div>
            </div>

            <div className={css.pricingTierControls}>
              <div className={css.pricingControlGroup}>
                <FieldLabel rootClassName={css.pricingControlLabel}>
                  <FormattedMessage id="EditListingCreatePackageForm.discountLabel" />
                </FieldLabel>
                <div className={css.percentageInputWrapper}>
                  <FieldNumberInput
                    className={css.percentageInput}
                    name="flexiblePricing.youthNonHunter.discount"
                    min={0}
                    max={100}
                    placeholder="100"
                    hideButtons
                  />
                  <span className={css.percentageSymbol}>%</span>
                </div>
              </div>
              <div className={css.pricingControlGroup}>
                <FieldLabel rootClassName={css.pricingControlLabel}>
                  <FormattedMessage id="EditListingCreatePackageForm.flexiblePricingQuantity" />
                </FieldLabel>
                <div className={css.quantityControls}>
                  <FieldNumberInput
                    className={css.quantityInput}
                    name="flexiblePricing.youthNonHunter.quantity"
                    min={0}
                    placeholder="1"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className={css.pricingTierPriceWrapper}>
            <div
              className={classNames(css.pricingTierPrice, {
                [css.pricingTierPriceDisabled]: flexiblePricingData.youthNonHunter.quantity === 0,
              })}
            >
              {youthNonHunterDiscount === 100 ? (
                <span className={css.freePrice}>
                  <FormattedMessage id="EditListingCreatePackageForm.freeLabel" />
                </span>
              ) : (
                formatMoney(intl, calculateDiscountedPrice(youthNonHunterDiscount))
              )}
            </div>
            <span
              className={classNames(css.pricingTierPriceUnit, {
                [css.pricingTierPriceUnitDisabled]:
                  flexiblePricingData.youthNonHunter.quantity === 0,
              })}
            >
              <FormattedMessage id="EditListingCreatePackageForm.perYouthNonHunterLabel" />
            </span>
          </div>
        </div>
      </EditListingSection>

      <EditListingSection hasBottomMargin isFullWidth rootClassName={css.listingSectionRoot}>
        {/* Reset to Defaults Button */}
        <InlineTextButton
          type="button"
          onClick={() => {
            form.change(
              'flexiblePricing.adultHunter.discount',
              flexiblePricingConfig.defaultValues.adultHunter.discount
            );
            form.change(
              'flexiblePricing.adultHunter.quantity',
              flexiblePricingConfig.defaultValues.adultHunter.quantity
            );
            form.change(
              'flexiblePricing.adultNonHunter.discount',
              flexiblePricingConfig.defaultValues.adultNonHunter.discount
            );
            form.change(
              'flexiblePricing.adultNonHunter.quantity',
              flexiblePricingConfig.defaultValues.adultNonHunter.quantity
            );
            form.change(
              'flexiblePricing.youthHunter.discount',
              flexiblePricingConfig.defaultValues.youthHunter.discount
            );
            form.change(
              'flexiblePricing.youthHunter.quantity',
              flexiblePricingConfig.defaultValues.youthHunter.quantity
            );
            form.change(
              'flexiblePricing.youthNonHunter.discount',
              flexiblePricingConfig.defaultValues.youthNonHunter.discount
            );
            form.change(
              'flexiblePricing.youthNonHunter.quantity',
              flexiblePricingConfig.defaultValues.youthNonHunter.quantity
            );
          }}
        >
          <div className={css.resetButton}>
            <RefreshIcon />
            <div>
              <FormattedMessage id="EditListingCreatePackageForm.resetToDefaultsLabel" />
            </div>
          </div>
        </InlineTextButton>
      </EditListingSection>
    </>
  );
};

const FieldFlexiblePricing = props => <FlexiblePricing {...props} />;

export default injectIntl(FieldFlexiblePricing);
